import React, { useState, useRef, useEffect } from "react";
import Table from "react-bootstrap/Table";
import { styles } from "../../styles/styles.js";
import { StarRating } from "../../components/starrating.js";
import { useAppState } from "../../AppStateContext.js";
import createScrollSnap from "scroll-snap";
import Card from "react-bootstrap/Card";
import * as motion from "motion/react-client";
import ReactMarkdown from "react-markdown";
import HorizontalBarChart from "../../components/Cards/barchart.js";
import { fmt, diff, stars, isNum, oddsToProb, vig } from "../../utils/format.js";

// Every probability, EV and vig in this file is a number or null. Null means the
// market or the model gave us nothing, and it stays null through every
// derivation until fmt() renders it as an em-dash.
//
// This used to be zeros. getHomeMoneylineProb returned 0 for an unpriced
// moneyline, so the Market row read 0%, EV collapsed to the model's entire
// probability, and calculateStars read that as a huge edge and awarded three
// stars — to BOTH teams, since it ignores its second argument once the first is
// positive. Missing odds produced the strongest buy signal the UI can emit.

const pct = (value) => fmt(value, 1, { suffix: "%" });
const evText = (value) => fmt(value, 2, { signed: true, suffix: "%" });

// A probability stored as a 0-1 fraction, rendered as a percentage.
const toPct = (fraction) => (isNum(fraction) ? fraction * 100 : null);

// The market's implied probability for each side of the spread. The guard is on
// the line being present, not truthy: a pick'em line of 0 is a real line, and
// the old `if (fixture.home_spread)` discarded it.
function getHomeSpreadProb(fixture) {
  return fixture.home_spread == null ? null : oddsToProb(fixture.home_spread_price);
}

function getAwaySpreadProb(fixture) {
  return fixture.away_spread == null ? null : oddsToProb(fixture.away_spread_price);
}


function Projections({ fixture, onClose }) {
  const [displayMode, setDisplayMode] = useState(2); // 2: Future State, 0: Probabilities, 1: EV
  const { isTutorialMode, toggleTutorialMode, isDesktopMode } = useAppState();
  const containerRef = useRef(null);

  useEffect(() => {
    if (isDesktopMode) return;
    const container = containerRef.current;
    if (container) {
      const { bind, unbind } = createScrollSnap(
        container,
        {
          snapDestinationY: "0%",
          timeout: 100,
          duration: 300,
          threshold: 0.2,
          snapStop: false,
          easing: (t) => t * (2 - t),
        },
        () => console.log("Element snapped")
      );

      bind();
      return () => unbind();
    }
  }, [isDesktopMode]);

  const toggleDisplayMode = () => {
    // setIsAutoCycling(false);
    setDisplayMode((prevMode) => (prevMode + 1) % 3);
  };

  if (fixture.ratings && fixture.insights) {
    const modelTypes = ["NeuralNetClassifier", "HistGradientBoostingClassifier"];

    const modelAliases = {
      NeuralNetClassifier: "Model A",
      HistGradientBoostingClassifier: "Model B",
    };

    // Pre-calculate all values. Every one of these is a number or null.
    const homeEloWinProb = toPct(fixture.ratings.home_elo_prob_ml);
    const awayEloWinProb = toPct(fixture.ratings.away_elo_prob_ml);
    const homeMoneylineProb = oddsToProb(fixture.home_moneyline);
    const awayMoneylineProb = oddsToProb(fixture.away_moneyline);
    const homeEloSpreadProb = toPct(fixture.ratings.home_elo_prob_spread);
    const awayEloSpreadProb = toPct(fixture.ratings.away_elo_prob_spread);
    const homeSpreadProb = getHomeSpreadProb(fixture);
    const awaySpreadProb = getAwaySpreadProb(fixture);

    const moneylineVig = vig(homeMoneylineProb, awayMoneylineProb);
    const spreadVig = vig(homeSpreadProb, awaySpreadProb);

    const marketOverProb = oddsToProb(fixture.over_price);
    const marketUnderProb = oddsToProb(fixture.under_price);
    const totalsVig = vig(marketOverProb, marketUnderProb);

    const modelProbabilities = modelTypes.map((model) => {
      const homeWinProb = toPct(fixture.insights[`home_win_prob_${model}`]);
      const awayWinProb = toPct(fixture.insights[`away_win_prob_${model}`]);
      const homeCoverProb = toPct(fixture.insights[`home_cover_prob_${model}`]);
      const awayCoverProb = toPct(fixture.insights[`away_cover_prob_${model}`]);
      const overProb = toPct(fixture.insights[`over_prob_${model}`]);
      const underProb = toPct(fixture.insights[`under_prob_${model}`]);

      return {
        model: modelAliases[model] || model.toUpperCase(),
        homeWinProb,
        awayWinProb,
        homeCoverProb,
        awayCoverProb,
        overProb,
        underProb,
        homeWinEV: diff(homeWinProb, homeMoneylineProb),
        awayWinEV: diff(awayWinProb, awayMoneylineProb),
        homeCoverEV: diff(homeCoverProb, homeSpreadProb),
        awayCoverEV: diff(awayCoverProb, awaySpreadProb),
        overEV: diff(overProb, marketOverProb),
        underEV: diff(underProb, marketUnderProb),
      };
    });

    const homeEloEV = diff(homeEloWinProb, homeMoneylineProb);
    const awayEloEV = diff(awayEloWinProb, awayMoneylineProb);
    const homeEloSpreadEV = diff(homeEloSpreadProb, homeSpreadProb);
    const awayEloSpreadEV = diff(awayEloSpreadProb, awaySpreadProb);

    // The blob is guarded above; the fields inside it are not, and after the
    // hydration change to null defaults they will routinely be absent.
    const homeEloRating = fmt(fixture.ratings.home_pre_elo, 0);
    const awayEloRating = fmt(fixture.ratings.away_pre_elo, 0);
    const homePointDiffRating = fmt(fixture.ratings.home_point_diff_rating, 0);
    const awayPointDiffRating = fmt(fixture.ratings.away_point_diff_rating, 0);

    const AnimatedCell = ({ children }) => (
      <motion.div
        key={displayMode}
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 0.5,
          scale: { type: "spring", damping: 10, stiffness: 100, bounce: 0.5 },
        }}>
        {children}
      </motion.div>
    );
    

    const containerStyle = isDesktopMode
      ? { overflowY: "visible", height: "auto" }
      : { overflowY: "scroll", height: "100vh", scrollSnapType: "y mandatory" };

    const sectionSnap = isDesktopMode
      ? { ...styles.sectionStyleDesktop }
      : { ...styles.sectionStyle, minHeight: "120vh", scrollSnapAlign: "start" };

    return (
      <div
        ref={containerRef}
        style={containerStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            position: "relative",
            padding: 0,
            margin: 0,
          }}>
          {isTutorialMode && !isDesktopMode && (
            <section
              style={{ ...sectionSnap, position: "relative", padding: 0, margin: 0 }}>
              <ul
                style={{
                  fontSize: "clamp(12px, calc(12px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                  marginBottom: "20px",
                  textAlign: "left",
                  listStyleType: "none",
                  paddingLeft: "0",
                  width: "100%",
                }}>
                <li>Swipe up to navigate</li>
              </ul>

              <div
                style={{
                  position: "absolute",
                  bottom: "25vh",
                  left: "50%",
                  transform: "translateX(-50%)",
                  margin: 0,
                  padding: 0,
                }}>
                <motion.svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  animate={{
                    y: [-80, 0, -80],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}>
                  <path d="M12 3l8 8h-6v10h-4v-10h-6z" fill="currentColor" />
                </motion.svg>
              </div>
            </section>
          )}

          {isTutorialMode && !isDesktopMode && (
            <section
              style={{ ...sectionSnap, position: "relative", padding: 0, margin: 0 }}>
              <ul
                style={{
                  fontSize: "clamp(12px, calc(12px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                  marginBottom: "20px",
                  textAlign: "left",
                  listStyleType: "none",
                  paddingLeft: "0",
                  width: "100%",
                }}>
                <li>• Tap the table to toggle between modes</li>
                <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                  <li>◦ Probabilities mode - show the probability predicted by the model</li>
                  <li>◦ EV (Expected Value) - the difference between the predicted probability and the market's probability</li>
                  <li>◦ Star mode - for fast evaluation of of EV</li>
                </ul>
                <li>
                  • Star ratings
                  <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                    <li>◦ ⭐ – Slight or no edge, we agree with the market pricing</li>
                    <li>◦ ⭐⭐ – Moderate value bet, we think it's a good bet for the price that the market offers</li>
                    <li>◦ ⭐⭐⭐ – High value bet, we think it has a much higher probability of success than the market has priced it</li>
                    <li>
                      ◦ Keep in mind that these reflect the EV. It is recommended to change to Probabilities mode to see the actual
                      probabilities and get a better understanding of the risks involved.
                    </li>
                  </ul>
                </li>
                <li>• How to interpret the data</li>
                <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                  <li>◦ Pay attention to whether the models agree with each other</li>
                  <li>◦ Use the vig to gauge the market's confidence in their model's predictions</li>
                </ul>
                <li>• Elo Ratings</li>
                <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                  <li>◦ Elo Ratings are tuned using Bayesian optimization to provide the most accurate ratings possible</li>
                </ul>
              </ul>
            </section>
          )}

          {isTutorialMode && !isDesktopMode && (
            <section
              style={{ ...sectionSnap, position: "relative", padding: 0, margin: 0 }}>
              <div
                style={{
                  marginBottom: "20px",
                  textAlign: "center",
                  padding: "20px",
                }}>
                <p style={styles.analysis}>
                  Done with the tutorial? Tap the button below to disable. You can always re-enable in the sidebar menu or in the profile
                  page
                </p>
              </div>
              <Card
                className="cardbutton"
                style={{ ...styles.cardbutton, width: "100%", marginBottom: "20px" }}
                onClick={toggleTutorialMode}>
                <Card.Text>Disable Tutorial Mode</Card.Text>
              </Card>
            </section>
          )}

          {/* Moneyline Group */}
          <section style={sectionSnap}>
            {displayMode !== 2 && (
              <Table bordered className="main" onClick={toggleDisplayMode}>
                <tbody>
                  <tr style={styles.Cells}>
                    <td colSpan="4" className="Cells" style={styles.column_1}>
                      Moneyline
                    </td>
                  </tr>
                  <tr>
                    <td className="Cells" style={styles.column_1}>
                      Model
                    </td>
                    <td className="Cells" style={styles.column_1}>
                      {fixture.home_team}
                    </td>
                    <td className="Cells" style={styles.column_1}>
                      {fixture.away_team}
                    </td>
                  </tr>
                  <tr style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      Elo
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      <AnimatedCell>
                        {displayMode === 2 ? (
                          <StarRating rating={stars(homeEloEV, awayEloEV, "ev")} />
                        ) : displayMode === 0 ? (
                          pct(homeEloWinProb)
                        ) : (
                          evText(homeEloEV)
                        )}
                      </AnimatedCell>
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      <AnimatedCell>
                        {displayMode === 2 ? (
                          <StarRating rating={stars(awayEloEV, homeEloEV, "ev")} />
                        ) : displayMode === 0 ? (
                          pct(awayEloWinProb)
                        ) : (
                          evText(awayEloEV)
                        )}
                      </AnimatedCell>
                    </td>
                  </tr>
                  {modelProbabilities.map(({ model, homeWinProb, awayWinProb, homeWinEV, awayWinEV }) => (
                    <tr key={`win_${model}`} style={styles.Cells}>
                      <td className="Cells" style={styles.column_1}>
                        {model}
                      </td>
                      <td className="Cells" style={styles.Cells}>
                        <AnimatedCell>
                          {displayMode === 2 ? (
                            <StarRating rating={stars(homeWinEV, awayWinEV, "ev")} />
                          ) : displayMode === 0 ? (
                            pct(homeWinProb)
                          ) : (
                            evText(homeWinEV)
                          )}
                        </AnimatedCell>
                      </td>
                      <td className="Cells" style={styles.Cells}>
                        <AnimatedCell>
                          {displayMode === 2 ? (
                            <StarRating rating={stars(awayWinEV, homeWinEV, "ev")} />
                          ) : displayMode === 0 ? (
                            pct(awayWinProb)
                          ) : (
                            evText(awayWinEV)
                          )}
                        </AnimatedCell>
                      </td>
                    </tr>
                  ))}
                  <tr style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      Market
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      {pct(homeMoneylineProb)}
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      {pct(awayMoneylineProb)}
                    </td>
                  </tr>
                  <tr style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      Moneyline Vig
                    </td>
                    <td className="Cells" style={styles.column_1}></td>
                    <td colSpan="2" className="Cells" style={styles.Cells}>
                      {pct(moneylineVig)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            )}
            {displayMode === 2 && (
              <div style={{
                width: "100%",
                marginTop: "0px", 
              }}>
              <Table bordered className="main" onClick={toggleDisplayMode}>
              <tbody>
                <tr style={styles.Cells}>
                  <td colSpan="4" className="Cells" style={styles.column_1}>
                    Moneyline
                  </td>
                </tr>
                <tr>
                  <td className="Cells" style={{...styles.column_1, width: "10%"}}>
                    Model
                  </td>
                  <td className="Cells" style={{...styles.column_1, width: "25%"}}>
                    {fixture.home_team}
                  </td>
                  <td className="Cells" style={{...styles.column_1, width: "25%"}}>
                    {fixture.away_team}
                  </td>
                </tr>

              </tbody>
            </Table>
              <HorizontalBarChart
                shortNames={["ELO", "A", "B", "MKT"]}
                rawData={[
                  diff(awayEloWinProb, homeEloWinProb),
                  diff(modelProbabilities[0].awayWinProb, modelProbabilities[0].homeWinProb),
                  diff(modelProbabilities[1].awayWinProb, modelProbabilities[1].homeWinProb),
                  diff(awayMoneylineProb, homeMoneylineProb),
                ]}
                names={["ELO", "Model A", "Model B", "Market"]}
                onClick={toggleDisplayMode}
              />

              </div>
            )}

            <div style={{ marginTop: "10vh" }}>
              {fixture.box_score && fixture.box_score["moneyline_projection"] && (
                <ul
                  style={{
                    listStyleType: "none",
                    padding: 0,
                    margin: 0,
                    fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                  }}>
                  <ReactMarkdown>{fixture.box_score["moneyline_projection"]}</ReactMarkdown>
                </ul>
              )}
            </div>
          </section>

          {/* Spread Group */}
          <section style={sectionSnap}>
            {displayMode !== 2 && (

            <Table bordered className="main" onClick={toggleDisplayMode}>
              <tbody>
                <tr style={styles.Cells}>
                  <td colSpan="4" className="Cells" style={styles.column_1}>
                    Spread
                  </td>
                </tr>
                <tr>
                  <td className="Cells" style={styles.column_1}>
                    Model
                  </td>
                  <td className="Cells" style={styles.column_1}>
                    {fixture.home_team}
                  </td>
                  <td className="Cells" style={styles.column_1}>
                    {fixture.away_team}
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Elo
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 2 ? (
                        <StarRating rating={stars(homeEloSpreadEV, awayEloSpreadEV, "ev")} />
                      ) : displayMode === 0 ? (
                        pct(homeEloSpreadProb)
                      ) : (
                        evText(homeEloSpreadEV)
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 2 ? (
                        <StarRating rating={stars(awayEloSpreadEV, homeEloSpreadEV, "ev")} />
                      ) : displayMode === 0 ? (
                        pct(awayEloSpreadProb)
                      ) : (
                        evText(awayEloSpreadEV)
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
                {modelProbabilities.map(({ model, homeCoverProb, awayCoverProb, homeCoverEV, awayCoverEV }) => (
                  <tr key={`cover_${model}`} style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      {model}
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      <AnimatedCell>
                        {displayMode === 2 ? (
                          <StarRating rating={stars(homeCoverEV, awayCoverEV, "ev")} />
                        ) : displayMode === 0 ? (
                          pct(homeCoverProb)
                        ) : (
                          evText(homeCoverEV)
                        )}
                      </AnimatedCell>
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      <AnimatedCell>
                        {displayMode === 2 ? (
                          <StarRating rating={stars(awayCoverEV, homeCoverEV, "ev")} />
                        ) : displayMode === 0 ? (
                          pct(awayCoverProb)
                        ) : (
                          evText(awayCoverEV)
                        )}
                      </AnimatedCell>
                    </td>
                  </tr>
                ))}
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Market
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {pct(homeSpreadProb)}
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {pct(awaySpreadProb)}
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Spread Vig
                  </td>
                  <td className="Cells" style={styles.column_1}></td>
                  <td colSpan="2" className="Cells" style={styles.Cells}>
                    {pct(spreadVig)}
                  </td>
                </tr>
              </tbody>
            </Table>
            )}
            {displayMode === 2 && (
              <div style={{
                width: "100%",
                marginTop: "0px", 
                alignSelf: "flex-start"
              }}>
              <Table bordered className="main" onClick={toggleDisplayMode}>
                <tbody>
                  <tr style={styles.Cells}>
                    <td colSpan="4" className="Cells" style={styles.column_1}>
                      Spread
                    </td>
                  </tr>
                  <tr>
                  <td className="Cells" style={{...styles.column_1, width: "10%"}}>
                      Model
                    </td>
                  <td className="Cells" style={{...styles.column_1, width: "25%"}}>
                      {fixture.home_team}
                    </td>
                  <td className="Cells" style={{...styles.column_1, width: "25%"}}>
                      {fixture.away_team}
                    </td>
                  </tr>
              </tbody>
              </Table>
              <HorizontalBarChart
                shortNames={["ELO", "A", "B", "MKT"]}
                rawData={[
                  diff(awayEloSpreadProb, homeEloSpreadProb),
                  diff(modelProbabilities[0].awayCoverProb, modelProbabilities[0].homeCoverProb),
                  diff(modelProbabilities[1].awayCoverProb, modelProbabilities[1].homeCoverProb),
                  diff(awaySpreadProb, homeSpreadProb),
                ]}
                names={["ELO", "Model A", "Model B", "Market"]}
                onClick={toggleDisplayMode}
              />

              </div>
            )}
            

            <div style={{ marginTop: "10vh" }}>
              {fixture.box_score && fixture.box_score["spread_projection"] && (
                <ul
                  style={{
                    listStyleType: "none",
                    padding: 0,
                    margin: 0,
                    fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                  }}>
                  <ReactMarkdown>{fixture.box_score["spread_projection"]}</ReactMarkdown>
                </ul>
              )}
            </div>
          </section>

          {/* Over/Under Group */}
          <section style={sectionSnap}>
            {displayMode !== 2 && (
            <Table bordered className="main" onClick={toggleDisplayMode}>
              <tbody>
                <tr style={styles.Cells}>
                  <td colSpan="4" className="Cells" style={styles.column_1}>
                    Over/Under
                  </td>
                </tr>
                <tr>
                  <td className="Cells" style={styles.column_1}>
                    Model
                  </td>
                  <td className="Cells" style={styles.column_1}>
                    Over
                  </td>
                  <td className="Cells" style={styles.column_1}>
                    Under
                  </td>
                </tr>
                {modelProbabilities.map(({ model, overProb, underProb, overEV, underEV }) => (
                  <tr key={`ou_${model}`} style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      {model}
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      <AnimatedCell>
                        {displayMode === 2 ? (
                          <StarRating rating={stars(overEV, underEV, "ev")} />
                        ) : displayMode === 0 ? (
                          pct(overProb)
                        ) : (
                          evText(overEV)
                        )}
                      </AnimatedCell>
                    </td>
                    <td className="Cells" style={styles.Cells}>
                      <AnimatedCell>
                        {displayMode === 2 ? (
                          <StarRating rating={stars(underEV, overEV, "ev")} />
                        ) : displayMode === 0 ? (
                          pct(underProb)
                        ) : (
                          evText(underEV)
                        )}
                      </AnimatedCell>
                    </td>
                  </tr>
                ))}
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Market
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {pct(marketOverProb)}
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {pct(marketUnderProb)}
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Totals Vig
                  </td>
                  <td className="Cells" style={styles.column_1}></td>
                  <td colSpan="2" className="Cells" style={styles.Cells}>
                    {pct(totalsVig)}
                  </td>
                </tr>
              </tbody>
            </Table>
            )}
            {displayMode === 2 && (
              <div style={{
                width: "100%",
                marginTop: "0px", 
                alignSelf: "flex-start"
              }}>
              <Table bordered className="main" onClick={toggleDisplayMode}>
                <tbody>
                  <tr style={styles.Cells}>
                    <td colSpan="4" className="Cells" style={styles.column_1}>
                      Over/Under
                    </td>
                  </tr>
                  <tr>
                    <td className="Cells" style={styles.column_1}>
                    </td>
                    <td className="Cells" style={styles.column_1}>
                      Over
                    </td>
                    <td className="Cells" style={styles.column_1}>
                      Under
                    </td>
                  </tr>
              </tbody>
              </Table>
              <HorizontalBarChart
                shortNames={["A", "B", "MKT"]}
                rawData={[
                  diff(modelProbabilities[0].underProb, modelProbabilities[0].overProb),
                  diff(modelProbabilities[1].underProb, modelProbabilities[1].overProb),
                  diff(marketUnderProb, marketOverProb),
                ]}
                names={["Model A", "Model B", "Market"]}
                onClick={toggleDisplayMode}
              />
            </div>
            )}
            <div style={{ marginTop: "10vh" }}>
              {fixture.box_score && fixture.box_score["over_projection"] && (
                <ul
                  style={{
                    listStyleType: "none",
                    padding: 0,
                    margin: 0,
                    fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                  }}>
                  <ReactMarkdown>{fixture.box_score["over_projection"]}</ReactMarkdown>
                </ul>
              )}
            </div>

          </section>

          {/* Other Ratings Group */}
          <section style={sectionSnap}>
            <Table bordered className="main">
              <tbody>
                <tr style={styles.Cells}>
                  <td colSpan="4" className="Cells" style={styles.column_1}>
                    Other Ratings
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Elo
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {homeEloRating}
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {awayEloRating}
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Point Differential Rating
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {homePointDiffRating}
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    {awayPointDiffRating}
                  </td>
                </tr>
              </tbody>
            </Table>
            {/* <div style={{ marginTop: "10vh" }}>
              {eloInsights.length > 0 && (
                <ul style={{ 
                  listStyleType: "none", 
                  padding: 0,
                  margin: 0 
                }}>
                  {eloInsights.map((insight, i) => (
                    <li key={i} style={styles.analysis}>
                      • {insight}
                    </li>
                  ))}
                </ul>
              )}
            </div> */}
          </section>

          {/* Close Button Section */}
          {onClose && !isDesktopMode && (
            <section style={sectionSnap}>
              <Card className="cardbutton" style={{ ...styles.cardbutton, width: "100%" }} onClick={onClose}>
                <Card.Text>Close Projections</Card.Text>
              </Card>
            </section>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default Projections;
